import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { usePortal } from '../../hooks/usePortal';
import TopTicker from './TopTicker';
import { RUSH_FROM } from '../../data/rush';

const BOOK = '/book'; // on-site booking page (embeds the calendar)

const services = [
  ['Bookkeeping & Accounting',      '/services/bookkeeping',  'Monthly books, reports & reconciliation'],
  ['Business & Individual Taxes',   '/services/tax-services', 'Tax returns & year-round planning'],
  ['Payroll Management',            '/services/payroll',      'Payroll, tax filings & year-end forms'],
  ['Business Formation & Consulting','/services/consulting',  'LLC, S-Corp & C-Corp setup'],
  ['Registered Agent',              '/services/registered-agent', 'State compliance & legal notices'],
  ['Bookkeeping Cleanup & Catch-Up','/services/bookkeeping-cleanup', 'Fix messy QuickBooks & catch up'],
  ['For Contractors',               '/services/contractors',  'Job costing, payroll & 1099s'],
  ['Foreign-Owned LLCs',            '/services/foreign-owned-llc-accounting', 'Form 5472, FBAR & visa-ready books'],
  ['Fractional CFO',                '/services/fractional-cfo', 'Forecasting, budgets & capital raising'],
  ['For Restaurants',               '/services/restaurants',  'Sales tax, tipped payroll & POS automation'],
];

// Everything in the Services dropdown: the services above plus Rush Service and the full list.
const megaItems = [
  ...services,
  ['Rush Service', '/rush-service', `Urgent deadlines, from ${RUSH_FROM}`],
  ['View all services', '/services', 'See everything we offer'],
];

const beforeServices = [
  ['Home',  '/'],
  ['About', '/about'],
];
const afterServices = [
  ['Industries', '/industries'],
  ['Blog',       '/blog'],
  ['Contact',    '/contact'],
];

export default function Header() {
  const [scrolled, setScrolled]     = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const { open } = usePortal();
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const close = () => setMobileOpen(false);

  const isActive = (path) => location.pathname === path ? 'active' : '';

  // All page heroes now use a light background, so the nav always renders dark
  // text (the white-on-dark `nav-hero` treatment is no longer needed).
  const navClass = `nav ${scrolled ? 'scrolled' : ''}`;

  return (
    <nav className={navClass} id="mainNav" role="navigation" aria-label="Main navigation">
      <div className="nav-inner">
        <Link to="/" className="nav-logo">
          <div className="nav-logo-icon">
            <img src="/logo.webp" alt="" width="52" height="52" />
          </div>
          <div>
            <div className="nav-logo-text">Bay Area Accounting</div>
            <div className="nav-logo-sub">Solutions</div>
          </div>
        </Link>

        <div className="nav-links" role="menubar">
          {beforeServices.map(([label, path]) => (
            <Link key={path} to={path} role="menuitem" className={`nav-link ${isActive(path)}`}>
              {label}
            </Link>
          ))}

          <div className="dropdown"
            onMouseEnter={() => setServicesOpen(true)}
            onMouseLeave={() => setServicesOpen(false)}
            role="none"
          >
            <button className="nav-link" aria-haspopup="true" aria-expanded={servicesOpen}>
              Services
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true" style={{transform:servicesOpen?'rotate(180deg)':'none',transition:'transform .2s'}}>
                <path d="M3 5l4 4 4-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              </svg>
            </button>
            <div className="dropdown-menu" style={{display:servicesOpen?'block':'none'}} role="menu">
              <div className="dropdown-inner dropdown-mega">
                {megaItems.map(([label, path, desc]) => (
                  <Link className={`dropdown-item${path === '/services' ? ' dropdown-all' : ''}`} to={path} role="menuitem" key={path}>
                    <span className="dropdown-title">{label}</span>
                    <span className="dropdown-desc">{desc}</span>
                  </Link>
                ))}
              </div>
            </div>
          </div>

          {afterServices.map(([label, path]) => (
            <Link key={path} to={path} role="menuitem" className={`nav-link ${isActive(path)}`}>
              {label}
            </Link>
          ))}
        </div>

        <div className="nav-cta">
          <a href="/client-portal/login" className="btn-g" onClick={(e) => { e.preventDefault(); open(); }} aria-label="Open client portal login">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
              <rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0110 0v4"/>
            </svg>
            Client Login
          </a>
          <a href={BOOK} className="btn-g nav-consult">Free Consultation</a>
          <Link to="/apply" className="btn-p">Apply Now</Link>
        </div>

        <button
          className="mob-toggle"
          onClick={() => setMobileOpen(o => !o)}
          aria-label="Toggle navigation menu"
          aria-expanded={mobileOpen}
        >
          <svg width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
            {mobileOpen ? <path d="M6 6l12 12M18 6L6 18"/> : <path d="M3 6h18M3 12h18M3 18h18"/>}
          </svg>
        </button>
      </div>

      <TopTicker />

      {mobileOpen && (
        <div className="mob-menu open" id="mobMenu" role="menu">
          <Link className="mob-link" to="/" onClick={close} role="menuitem">Home</Link>
          <Link className="mob-link" to="/about" onClick={close} role="menuitem">About</Link>
          <div className="mob-sub-label">Services</div>
          {services.map(([label, path]) => (
            <Link className="mob-sub-link" to={path} onClick={close} role="menuitem" key={path}>{label}</Link>
          ))}
          <Link className="mob-sub-link" to="/rush-service" onClick={close} role="menuitem">Rush Service</Link>
          <Link className="mob-sub-link" to="/services" onClick={close} role="menuitem">View all services</Link>
          <Link className="mob-link" to="/industries" onClick={close} role="menuitem">Industries</Link>
          <Link className="mob-link" to="/blog" onClick={close} role="menuitem">Blog</Link>
          <Link className="mob-link" to="/contact" onClick={close} role="menuitem">Contact</Link>
          <div style={{marginTop:'20px',display:'flex',flexDirection:'column',gap:'12px'}}>
            <a href="/client-portal/login" className="btn-g" style={{justifyContent:'center',padding:'12px'}} onClick={(e) => { e.preventDefault(); close(); open(); }}>Client Login</a>
            <Link to="/apply" className="btn-p" style={{justifyContent:'center',padding:'12px'}} onClick={close}>Apply Now</Link>
            <a href={BOOK} className="btn-g" style={{justifyContent:'center',padding:'12px'}} onClick={close}>Free Consultation</a>
          </div>
        </div>
      )}
    </nav>
  );
}
